/** Regula de bani a aplicatiei, intr-un singur loc (decizia userului, 31.08.2026):
 *
 *  1. Sumele se arata PERMANENT in ambele monede, pe facturi si in rapoarte.
 *  2. Cursul de conversie:
 *       - la VANZARE: cursul BNR + 2% (cel pe care il da API-ul in `eur_rate`; verificat
 *         ca include deja marja: 5,3636 = 5,2584 BNR x 1,02).
 *       - la CUMPARARE: cursul de pe documentul FURNIZORULUI (`exchange_rate` de pe factura).
 *         Doar daca lipseste se cade pe BNR+2%.
 *  3. Sub 25 de lei restul nu e datorie, e reziduu de curs — nu se afiseaza ca „de incasat".
 */
export const PRAG_REZIDUU_LEI = 25

export type DocValutar = {
  currency: string
  exchange_rate: number | null
  invoice_type?: string | null
}

/** Cursul e SELECTABIL (cerere user, 31.08.2026): omul alege pe ce baza se face conversia,
 *  fiindca banca deconteaza la cursul ei, nu la cel pe care il presupunem noi. */
export type ModCurs = 'auto' | 'bnr2' | 'bnr' | 'document' | 'manual'

export const MODURI: { id: ModCurs; label: string; explicatie: string }[] = [
  { id: 'auto', label: 'Automat', explicatie: 'BNR+2% la vanzare, cursul furnizorului la cumparare' },
  { id: 'bnr2', label: 'BNR + 2%', explicatie: 'cursul de vanzare al agentiei, cel de azi' },
  { id: 'bnr', label: 'BNR', explicatie: 'cursul oficial al zilei, fara marja' },
  { id: 'document', label: 'Cursul documentului', explicatie: 'cursul cu care a fost emis documentul' },
  { id: 'manual', label: 'Curs introdus', explicatie: 'cursul chiar obtinut la banca' },
]

export type ContextCurs = { bnrPlus2: number; mod: ModCurs; manual?: number | null }

/** Cursul de referinta al documentului, dupa modul ales.
 *  `bnrPlus2` vine din API (`eur_rate`) si include deja marja: 5,3636 = 5,2584 x 1,02,
 *  deci BNR pur se obtine impartind la 1,02. */
export function cursDeReferinta(doc: DocValutar, ctx: ContextCurs): number {
  const { bnrPlus2, mod, manual } = ctx
  const eLei = (doc.currency || 'RON').toUpperCase() === 'RON'
  const eCumparare = (doc.invoice_type || '').toLowerCase().startsWith('purch')
  const cursDoc = doc.exchange_rate && doc.exchange_rate > 1.01 ? doc.exchange_rate : null

  if (mod === 'manual' && manual && manual > 0) return manual
  if (mod === 'bnr') return bnrPlus2 / 1.02
  if (mod === 'bnr2') return bnrPlus2
  if (mod === 'document') return cursDoc ?? bnrPlus2
  // auto: cursul furnizorului DOAR pe un document in valuta (pe unul in lei ar da 1:1 —
  // verificat pe cumpararea 56008749, care are exchange_rate = 1.0 desi e in lei).
  if (!eLei && eCumparare && cursDoc) return cursDoc
  return bnrPlus2
}

/** Sursa cursului, ca omul sa stie de ce vede cifra aia. */
export function sursaCurs(doc: DocValutar, ctx: ContextCurs): string {
  const c = cursDeReferinta(doc, ctx)
  const eLei = (doc.currency || 'RON').toUpperCase() === 'RON'
  const eCumparare = (doc.invoice_type || '').toLowerCase().startsWith('purch')
  const cursDoc = doc.exchange_rate && doc.exchange_rate > 1.01 ? doc.exchange_rate : null
  const sursa =
    ctx.mod === 'manual' ? 'curs introdus'
    : ctx.mod === 'bnr' ? 'BNR, fara marja'
    : ctx.mod === 'bnr2' ? 'BNR + 2%'
    : ctx.mod === 'document' ? (cursDoc ? 'cursul documentului' : 'documentul nu are curs — BNR + 2%')
    : !eLei && eCumparare && cursDoc ? 'cursul de pe factura furnizorului'
    : 'BNR + 2%'
  return `1 EUR = ${c.toFixed(4)} lei (${sursa})`
}

export const lei = (n: number) =>
  new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

export type Pereche = { moneda: string; valuta: number; inLei: number; curs: number }

/** Perechea de afisat, pornind de la o suma in moneda documentului. */
export function pereche(valoare: number, doc: DocValutar, ctx: ContextCurs): Pereche {
  const c = cursDeReferinta(doc, ctx)
  const moneda = (doc.currency || 'RON').toUpperCase()
  return moneda === 'RON'
    ? { moneda, inLei: valoare, valuta: c ? valoare / c : 0, curs: c }
    : { moneda, valuta: valoare, inLei: valoare * c, curs: c }
}

/** Aceeasi pereche, pornind de la o suma care vine deja in LEI din API (ex. restant_ron). */
export function perecheDinLei(sumaLei: number, doc: DocValutar, ctx: ContextCurs): Pereche {
  const c = cursDeReferinta(doc, ctx)
  const moneda = (doc.currency || 'RON').toUpperCase()
  return { moneda, inLei: sumaLei, valuta: c ? sumaLei / c : 0, curs: c }
}

/** Sub prag nu e datorie, e reziduu de curs. */
export const eDatorie = (restLei: number) => restLei >= PRAG_REZIDUU_LEI
